import { LitElement, html, css } from "lit";
import { customElement, property, state, query } from "lit/decorators.js";
import { consume } from "@lit/context";
import iro from "@jaames/iro";
import { configContext } from "../contexts";
import type { RatingSet, RatingConfig } from "../models";

/**
 * Tab list for managing multiple comparisons.
 * @fires tab-changed - When a tab is selected (detail: { index: number }).
 * @fires add-dataset - When the "+" button is clicked.
 * @fires duplicate-dataset - When the duplicate icon is clicked (detail: { index: number }).
 * @fires remove-dataset - When the "X" icon is clicked (detail: { index: number }).
 * @fires open-import - When "Import Comparison" is selected from the dropdown.
 */
@customElement("bab-subject-tabs")
export class BabSubjectTabs extends LitElement {
  static styles = css`
    :host {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 16px;
      position: relative;
      z-index: 100;
    }

    .tabs-list {
      display: flex;
      gap: 8px;
      overflow-x: auto;
      padding-bottom: 4px;
      scrollbar-width: thin;
      min-width: 0;
    }

    .subject-tab {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 12px;
      background: var(--bab-card-bg);
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: 20px;
      font-size: 0.8rem;
      font-weight: 600;
      font-family: inherit;
      color: var(--bab-text-secondary);
      cursor: pointer;
      white-space: nowrap;
      transition: all 0.2s;
    }

    .subject-tab:focus-visible {
      outline: 2px solid var(--bab-accent, #4a90e2);
      outline-offset: 2px;
    }

    .subject-tab--active {
      background: var(--bab-accent, #4a90e2);
      color: #fff;
      border-color: var(--bab-accent, #4a90e2);
    }

    .subject-tab-color {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: currentColor;
      cursor: pointer;
      transition: transform 0.2s;
    }

    .subject-tab-color:hover {
      transform: scale(1.2);
    }

    :host([locked]) .subject-tab-color {
      cursor: default;
      pointer-events: none;
    }

    .subject-tab-action {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.08);
      color: inherit;
      font-size: 10px;
      transition: background 0.2s, transform 0.1s, opacity 0.2s, visibility 0.2s;
    }

    .subject-tab-action--hidden {
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
    }

    .subject-tab-action:hover:not(.subject-tab-action--hidden) {
      background: rgba(0, 0, 0, 0.15);
      transform: scale(1.1);
    }

    .subject-tab-remove {
      margin-left: -2px;
    }

    .subject-tab-duplicate {
      margin-left: 4px;
    }

    .add-comparison-group {
      display: flex;
      position: relative;
      gap: 0;
    }

    .add-subject-btn {
      padding: 6px 14px;
      border-radius: 20px 0 0 20px;
      background: transparent;
      border: var(--bab-border-width, 1.5px) dashed var(--bab-border);
      border-right: none;
      color: var(--bab-text-secondary);
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      white-space: nowrap;
    }

    .add-subject-btn:hover {
      border-color: var(--bab-accent, #4a90e2);
      color: var(--bab-accent, #4a90e2);
      background: rgba(74, 144, 226, 0.05);
    }

    .add-dropdown-toggle {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      padding: 0;
      border-radius: 0 20px 20px 0;
      background: transparent;
      border: var(--bab-border-width, 1.5px) dashed var(--bab-border);
      color: var(--bab-text-secondary);
      cursor: pointer;
      transition: all 0.2s;
    }

    .add-dropdown-toggle:hover {
      border-color: var(--bab-accent, #4a90e2);
      color: var(--bab-accent, #4a90e2);
      background: rgba(74, 144, 226, 0.05);
    }

    .add-dropdown-toggle svg {
      width: 14px;
      height: 14px;
    }

    .add-dropdown-menu {
      position: absolute;
      top: calc(100% + 4px);
      right: 0;
      z-index: 100;
      min-width: 180px;
      background: var(--bab-card-bg);
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: var(--bab-radius, 12px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      padding: 4px;
      opacity: 0;
      visibility: hidden;
      transform: translateY(-8px);
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .add-dropdown-menu--visible {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }

    .add-dropdown-item {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 8px 12px;
      border: none;
      background: transparent;
      color: var(--bab-text);
      font-size: 0.85rem;
      font-weight: 500;
      text-align: left;
      cursor: pointer;
      border-radius: 4px;
      transition: background 0.15s;
    }

    .add-dropdown-item:hover {
      background: var(--bab-bg, #f0f4f8);
    }

    .add-dropdown-item svg {
      width: 16px;
      height: 16px;
      color: var(--bab-text-secondary);
    }

    #iro-picker {
      display: flex;
      justify-content: center;
    }

    /* Polish for the popover */
    .color-picker-popover {
      position: absolute;
      top: calc(100% + 4px);
      z-index: 200;
      background: var(--bab-card-bg);
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: var(--bab-radius, 12px);
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
      padding: 16px;
      opacity: 0;
      visibility: hidden;
      transform: translateY(-8px);
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .color-picker-popover--visible {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }

    .color-picker-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      font-size: 0.8rem;
      font-weight: 700;
      color: var(--bab-text);
    }

    .close-picker {
      cursor: pointer;
      padding: 4px;
      color: var(--bab-text-secondary);
      line-height: 1;
    }
    `;

  @consume({ context: configContext, subscribe: true })
  @state()
  config!: RatingConfig;

  @property({ type: Array }) datasets: RatingSet[] = [];
  @property({ type: Number }) activeIndex = 0;
  @property({ type: Boolean, reflect: true }) locked = false;
  @state() private _showAddDropdown = false;
  @state() private _showColorPicker = false;
  @state() private _colorPickerIndex = -1;
  @state() private _colorPickerLeft = 0;

  @query("#iro-picker") private _pickerContainer!: HTMLDivElement;

  private _iroInstance: iro.ColorPicker | null = null;


  private _onWindowClick = (e: MouseEvent) => {
    const path = e.composedPath();
    if (this._showAddDropdown && !path.some(el => (el as Element).classList?.contains("add-comparison-group"))) {
      this._showAddDropdown = false;
    }
    if (this._showColorPicker &&
        !path.some(el => (el as Element).classList?.contains("color-picker-popover")) &&
        !path.some(el => (el as Element).classList?.contains("subject-tab-color"))) {
      this._showColorPicker = false;
    }
  };

  private _onColorClick(index: number, e: Event) {
    if (this.locked) return;
    e.stopPropagation();
    
    if (this._showColorPicker && this._colorPickerIndex === index) {
      this._showColorPicker = false;
      return;
    }

    this._colorPickerIndex = index;
    this._showColorPicker = true;
    
    // Position the popover near the clicked dot
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const hostRect = this.getBoundingClientRect();
    this._colorPickerLeft = rect.left - hostRect.left;

    this._initIro(this.datasets[index].color || this.config.datasetColors[index % this.config.datasetColors.length]);
  }

  private _initIro(initialColor: string) {
    setTimeout(() => {
      if (!this._pickerContainer) return;
      
      if (this._iroInstance) {
        this._iroInstance.off("color:change", this._onColorChange);
        this._pickerContainer.innerHTML = "";
      }

      this._iroInstance = iro.ColorPicker(this._pickerContainer, {
        width: 150,
        color: initialColor,
        layout: [
          { component: iro.ui.Wheel },
          { component: iro.ui.Slider, options: { sliderType: "hue" } },
        ]
      });

      this._iroInstance.on("color:change", this._onColorChange);
    }, 0);
  }

  private _onColorChange = (color: iro.Color) => {
    if (this._colorPickerIndex === -1) return;
    this.dispatchEvent(new CustomEvent("color-changed", {
      detail: { 
        index: this._colorPickerIndex, 
        color: color.hexString 
      }
    }));
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("click", this._onWindowClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("click", this._onWindowClick);
  }

  private _onTabClick(index: number) {
    this.dispatchEvent(new CustomEvent("tab-changed", { detail: { index } }));
  }

  private _onKeyDown(e: KeyboardEvent, index: number) {
    const count = this.datasets.length;
    if (e.key === "ArrowRight") {
      const next = (index + 1) % count;
      this._onTabClick(next);
      this._focusTab(next);
    } else if (e.key === "ArrowLeft") {
      const prev = (index - 1 + count) % count;
      this._onTabClick(prev);
      this._focusTab(prev);
    } else if (e.key === "Delete" || e.key === "Backspace") {
      this._handleRemove(index, e);
    } else if ((e.key === "d" || e.key === "D") && !this.locked) {
      e.preventDefault();
      this._handleDuplicate(index, e);
    }
  }

  private _focusTab(index: number) {
    this.updateComplete.then(() => {
      const tabs = this.shadowRoot!.querySelectorAll(".subject-tab");
      (tabs[index] as HTMLElement)?.focus();
    });
  }

  private _handleAdd() {
    this.dispatchEvent(new CustomEvent("add-dataset"));
  }

  private _handleDuplicate(index: number, e: Event) {
    e.stopPropagation();
    this.dispatchEvent(new CustomEvent("duplicate-dataset", { detail: { index } }));
  }

  private _handleRemove(index: number, e: Event) {
    e.stopPropagation();
    this.dispatchEvent(new CustomEvent("remove-dataset", { detail: { index } }));
  }

  private _handleImport() {
    this._showAddDropdown = false;
    this.dispatchEvent(new CustomEvent("open-import"));
  }

  private _onDropdownKeyDown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      this._showAddDropdown = false;
      this.shadowRoot?.querySelector<HTMLElement>(".add-dropdown-toggle")?.focus();
    }
  }

  render() {
    return html`
      <div class="tabs-list" role="tablist">
        ${this.datasets.map((set, i) => html`
          <button 
            role="tab"
            aria-selected="${i === this.activeIndex}"
            tabindex="${i === this.activeIndex ? 0 : -1}"
            class="subject-tab ${i === this.activeIndex ? "subject-tab--active" : ""}"
            @click="${() => this._onTabClick(i)}"
            @keydown="${(e: KeyboardEvent) => this._onKeyDown(e, i)}"
            >
            <span 
              class="subject-tab-color" 
              style="color: ${set.color || this.config.datasetColors[i % this.config.datasetColors.length]}"
              @click="${(e: Event) => this._onColorClick(i, e)}"
              title="${this.locked ? "" : "Change color"}"
            ></span>
            ${set.name || `${i + 1}`}

            <span
                class="subject-tab-action subject-tab-duplicate ${this.locked ? "subject-tab-action--hidden" : ""}"
                title="Duplicate comparison (D)"
                @click="${(e: Event) => this._handleDuplicate(i, e)}"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
            </span>
            <span
                class="subject-tab-action subject-tab-remove ${this.locked || this.datasets.length <= 1 ? "subject-tab-action--hidden" : ""}"
                title="Remove comparison (Delete)"
                @click="${(e: Event) => this._handleRemove(i, e)}"
            >✕</span>
          </button>
        `)}
      </div>
      <div class="add-comparison-group">
        <button class="add-subject-btn" @click="${this._handleAdd}" aria-label="Add new comparison">+ Add Comparison</button>
        <button 
          class="add-dropdown-toggle" 
          @click="${(e: Event) => { e.stopPropagation(); this._showAddDropdown = !this._showAddDropdown; }}" 
          aria-label="More options"
          aria-haspopup="true"
          aria-expanded="${this._showAddDropdown}"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
        <div 
          class="add-dropdown-menu ${this._showAddDropdown ? "add-dropdown-menu--visible" : ""}"
          @keydown="${this._onDropdownKeyDown}"
        >
          <button class="add-dropdown-item" @click="${this._handleImport}" role="menuitem">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Import Comparison
          </button>
        </div>
      </div>

      <div 
        class="color-picker-popover ${this._showColorPicker ? "color-picker-popover--visible" : ""}"
        style="left: ${this._colorPickerLeft}px"
      >
        <div class="color-picker-header">
          <span>Pick Color</span>
          <span class="close-picker" @click="${() => this._showColorPicker = false}">✕</span>
        </div>
        <div id="iro-picker"></div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bab-subject-tabs": BabSubjectTabs;
  }
}
